import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import { Box, CircularProgress } from "@mui/material";
import { Timeline, TimelineConnector, TimelineContent, TimelineItem, TimelineOppositeContent, TimelineSeparator } from "@mui/lab";

import { AnglerEvents, AnglerTagSettings, getInitialTagSettings, getRouteBetweenCalls, VoyagePlanEvent } from "./AnglerUtils";
import AnglerTags from "./components/tags/AnglerTags";
import AnglerTimelineCallEvent from "./components/events/AnglerTimelineCallEvent";
import AnglerTimelineWaitingEvent from "./components/events/AnglerTimelineWaitingEvent";
import AnglerTimelineBridgeEvent from "./components/events/AnglerTimelineBridgeEvent";
import AnglerTimelineLockEvent from "./components/events/AnglerTimelineLockEvent";
import AnglerTimelineMeasuringPointEvent from "./components/events/AnglerTimelineMeasuringPointEvent";

import { GetVoyagePlanDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { KeelClearanceStatus, VoyageEventType } from "__generated__/enums";

const EVENT_COMPONENTS: Partial<Record<VoyageEventType, React.ComponentType<{ event: VoyagePlanEvent }>>> = {
	[VoyageEventType.Waiting]: AnglerTimelineWaitingEvent as React.ComponentType<{ event: VoyagePlanEvent }>,
	[VoyageEventType.Bridge]: AnglerTimelineBridgeEvent as React.ComponentType<{ event: VoyagePlanEvent }>,
	[VoyageEventType.Lock]: AnglerTimelineLockEvent as React.ComponentType<{ event: VoyagePlanEvent }>,
	[VoyageEventType.MeasuringPoint]: AnglerTimelineMeasuringPointEvent as React.ComponentType<{ event: VoyagePlanEvent }>,
};

const Angler = ({ voyageId }: { voyageId: number }) => {
	const [ tagSettings, setTagSettings ] = useState<AnglerTagSettings>(getInitialTagSettings());

	const { data, loading }  = useQuery(GetVoyagePlanDocument, {
		variables: {
			id: voyageId,
		},
	});

	const events = data?.voyagePlan?.events ?? [];

	const toggleEvent = (name: AnglerEvents) => {
		setTagSettings((prev) => ({
			...prev,
			[name]: { ...prev[name], visible: !prev[name].visible },
		}));
	};

	const tagAmounts = useMemo(() => {
		return {
			[AnglerEvents.Waiting]: events.filter(e => e.type === VoyageEventType.Waiting).length,
			[AnglerEvents.Bridge]: events.filter(e => e.type === VoyageEventType.Bridge).length,
			[AnglerEvents.Lock]: events.filter(e => e.type === VoyageEventType.Lock).length,
			[AnglerEvents.MeasuringPoint]: events.filter(e => e.type === VoyageEventType.MeasuringPoint).length,
			[AnglerEvents.Alert]: events.filter(e => e.__typename === "MeasuringPointEvent" && e.warning && e.keelClearanceStatus === KeelClearanceStatus.Red).length,
			[AnglerEvents.Warning]: events.filter(e => e.__typename === "MeasuringPointEvent" && e.warning && e.keelClearanceStatus === KeelClearanceStatus.Orange).length,
			[AnglerEvents.Unknown]: events.filter(e => e.__typename === "MeasuringPointEvent" && e.warning && e.keelClearanceStatus === KeelClearanceStatus.Unknown).length,
		};
	}, [ events ]);

	const isEventVisible = (event: VoyagePlanEvent): boolean => {
		switch (event.type) {
			case VoyageEventType.Waiting:
				return tagSettings.waiting.visible;
			case VoyageEventType.Bridge:
				return tagSettings.bridge.visible;
			case VoyageEventType.Lock:
				return tagSettings.lock.visible;
			case VoyageEventType.MeasuringPoint:
				if (event.__typename === "MeasuringPointEvent") {
					if (tagSettings.measuringPoint.visible) {
						return true;
					}
					if (tagSettings.alert.visible && event.keelClearanceStatus === KeelClearanceStatus.Red) {
						return true;
					}
					if (tagSettings.warning.visible && event.keelClearanceStatus === KeelClearanceStatus.Orange) {
						return true;
					}
					if (tagSettings.unknown.visible && event.keelClearanceStatus === KeelClearanceStatus.Unknown) {
						return true;
					}
				}
				return false;
			default:
				return false;
		}
	};


	if (loading) {
		return <CircularProgress size={24} />;
	}

	const allTagsDisabled = Object.values(tagSettings).every(tag => !tag.visible);

	return (
		<Box sx={{ width: "100%" }}>
			<AnglerTags
				settings={tagSettings}
				onToggle={toggleEvent}
				amounts={tagAmounts} />

			<Timeline sx={{ p: 0 }}>
				{events.map((event, index) => {
					if (event.type !== VoyageEventType.Call && !isEventVisible(event)) {
						return null;
					}

					if (event.type === VoyageEventType.Call && event.__typename === "CallEvent") {
						const route = getRouteBetweenCalls(event, events);
						const nextCallIndex = events.findIndex((e, i) => i > index && e.__typename === "CallEvent");
						const rawEventsBetween = nextCallIndex !== -1 ? events.slice(index + 1, nextCallIndex) : [];
						const hasVisibleEventsBetween = rawEventsBetween.some(isEventVisible);

						return (
							<React.Fragment key={index}>
								<AnglerTimelineCallEvent
									event={event}
									events={events}
									allTagsDisabled={allTagsDisabled} />

								{!allTagsDisabled && route.nextCall && !hasVisibleEventsBetween && (
									<TimelineItem sx={{ minHeight: 40 }}>
										<TimelineOppositeContent />
										<TimelineSeparator>
											<TimelineConnector />
										</TimelineSeparator>
										<TimelineContent />
									</TimelineItem>
								)}
							</React.Fragment>
						);
					}

					if (event.type !== VoyageEventType.Call) {
						const TargetComponent = EVENT_COMPONENTS[event.type];

						if (!TargetComponent) {
							return null;
						}

						return <TargetComponent key={index} event={event} />;
					}

					return null;
				})}
			</Timeline>
		</Box>
	);
};

export default Angler;
